import { ChordLib } from '@tunasong/music-lib'
import type { Chord, NoteEvent, VoicedChord } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'

/**
 * Build NoteEvents for one chord at a given octave + duration.
 * Used by stab-scheduler-driven playback hooks across the music-theory tools.
 */
export const chordToEvents = (
  chord: Chord,
  duration: NoteEvent['duration'],
  octave = 4
): NoteEvent[] =>
  ChordLib.getNotes(chord).map(n => ({
    type: 'note' as const,
    note: `${n}${octave}` as NoteName,
    duration,
  }))

/**
 * Build NoteEvents from an explicit per-voice voicing (pitches low → high, each
 * already octave-qualified). Generalises the `octave = 4` workaround in
 * `chordToEvents` for tools that need precise voicing/inversion control —
 * Voice-Leading Lab today, Modulation Lab v1.5 track #1 later.
 *
 * Pitches are emitted in voicing order, preserving implicit inversion.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §8.5
 */
export const voiceLeadingChordToEvents = (
  chord: VoicedChord,
  duration: NoteEvent['duration']
): NoteEvent[] =>
  chord.pitches.map(note => ({
    type: 'note' as const,
    note,
    duration,
  }))
